import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FileDown, Download, Copy } from "lucide-react";
import { useAutoTranslate, AutoTranslatedBadge } from "@/lib/loungeTranslate";
import { generateReleasePdf } from "@/lib/releasePdf";
import { formatReleaseDate } from "@/lib/releaseDate";
import logoPng from "@/assets/brema-logo.png";
import { usePressGate, triggerDownload } from "@/components/lounge/PressAccreditationGate";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Pagina di dettaglio "Brema Media Release": modal-documento su sfondo bianco pulito
// (nessuna immagine di sfondo). Logo → Data/Categoria → Titolo → Testo integrale
// (tradotto AI) → Contatti PR in calce. Il PDF ufficiale viene generato al volo.
export function ReleaseDetailDialog({ release, open, onOpenChange, contacts = {}, c, lang }) {
  const body = Array.isArray(release?.body) ? release.body : [];
  const needTr = open && lang !== "it";
  const src = needTr ? [release?.title || "", release?.category || "", release?.excerpt || "", ...body] : [];
  const { translated, translating } = useAutoTranslate(src, lang);
  const { gate } = usePressGate();

  if (!release) return null;

  const ready = needTr && !translating;
  const tTitle = ready ? (translated[0] ?? release.title) : release.title;
  const tCategory = ready ? (translated[1] ?? release.category) : release.category;
  const tBody = ready ? body.map((p, i) => translated[3 + i] ?? p) : body;
  const dateStr = formatReleaseDate(release.date, lang);
  const assets = Array.isArray(release?.assets) ? release.assets : [];

  const contactList = ["office", "contact", "email", "phone", "website", "other"]
    .map((k) => (contacts[k] || "").trim())
    .filter(Boolean);

  const downloadPdf = () => {
    gate(() => generateReleasePdf({
      title: tTitle, category: tCategory, date: dateStr, body: tBody,
      contacts, labels: { release_detail_title: c.release_detail_title, press_contacts: c.press_contacts },
    }), { type: "download", item: { kind: "release_pdf", id: release?.id, title: release?.title || tTitle, filename: "comunicato.pdf" } });
  };

  const copyText = async () => {
    const full = [tTitle, "", ...tBody].join("\n").trim();
    try { await navigator.clipboard.writeText(full); toast.success(c.copied); }
    catch { toast.error(c.copy_failed); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto border border-neutral-200 bg-white p-0 text-neutral-900" data-testid="release-detail-dialog">
        <DialogHeader className="sr-only">
          <DialogTitle>{tTitle}</DialogTitle>
          <DialogDescription>{c.release_detail_title}</DialogDescription>
        </DialogHeader>

        {/* Testata ufficiale */}
        <div className="flex items-center justify-between border-b border-neutral-200 px-6 py-5 sm:px-10">
          <img src={logoPng} alt="Brema 1969" className="h-16 w-auto" data-testid="release-detail-logo" />
          <span className="text-[11px] font-bold uppercase tracking-widest text-neutral-400">{c.release_detail_title}</span>
        </div>

        <article className="px-6 py-8 sm:px-10">
          <div className="mb-3 flex flex-wrap items-center gap-3">
            <span className="text-xs font-bold uppercase tracking-wide text-[#8a1c34]" data-testid="release-detail-category">
              {[tCategory, dateStr].filter(Boolean).join("   ·   ")}
            </span>
            {needTr && <AutoTranslatedBadge translating={translating} labels={c} testId="release-detail-translated-badge" />}
          </div>

          <h1 className="text-2xl font-bold leading-tight text-neutral-900 sm:text-3xl" data-testid="release-detail-title">{tTitle}</h1>

          <div className="mt-6 space-y-4 border-l-2 border-[#8a1c34]/25 pl-5" data-testid="release-detail-body">
            {tBody.length > 0 ? (
              tBody.map((p, i) => (
                <p key={i} className="text-[15px] leading-relaxed text-neutral-700">{p}</p>
              ))
            ) : (
              <p className="text-[15px] leading-relaxed text-neutral-700">{ready ? (translated[2] ?? release.excerpt) : release.excerpt}</p>
            )}
          </div>

          {/* Contatti PR / firma */}
          {contactList.length > 0 && (
            <div className="mt-10 border-t border-neutral-200 pt-5" data-testid="release-detail-contacts">
              <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-[#8a1c34]">{c.press_contacts}</h3>
              <div className="space-y-0.5 text-sm text-neutral-600">
                {contactList.map((ln, i) => (
                  <p key={i} className={i === 0 ? "font-semibold text-neutral-900" : ""}>{ln}</p>
                ))}
              </div>
            </div>
          )}

          {/* Mini-galleria media (foto/video singoli) */}
          {assets.length > 0 && (
            <div className="mt-9 border-t border-neutral-200 pt-5" data-testid="release-detail-gallery">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-xs font-bold uppercase tracking-wide text-[#8a1c34]">{c.release_gallery_title}</h3>
                {release.zip_url && (
                  <button type="button" onClick={() => gate(() => triggerDownload(`${BACKEND}${release.zip_url}`), { type: "download", item: { kind: "release_zip", id: release?.id, title: release?.title, filename: "media-kit.zip" } })} className="inline-flex items-center gap-1.5 rounded-full border border-neutral-300 px-3 py-1 text-xs font-semibold text-neutral-800 hover:bg-neutral-100" data-testid="release-detail-download-all-zip">
                    <Download className="h-3.5 w-3.5" /> {c.download_all_zip}
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {assets.map((a, i) => (
                  <div key={a.id} className="overflow-hidden rounded-lg border border-neutral-200 bg-neutral-50" data-testid={`release-asset-${i}`}>
                    {a.kind === "video" ? (
                      <video src={`${BACKEND}${a.url}`} controls preload="metadata" className="aspect-video w-full bg-black object-cover" data-testid={`release-asset-video-${i}`} />
                    ) : (
                      <img src={`${BACKEND}${a.url}`} alt={a.filename || `media-${i}`} loading="lazy" className="aspect-video w-full object-cover" data-testid={`release-asset-image-${i}`} />
                    )}
                    <button type="button" onClick={() => gate(() => triggerDownload(`${BACKEND}${a.download_url}`), { type: "download", item: { kind: "release_asset", id: release?.id, title: `${release?.title || ""} · ${a.filename || "media"}`, filename: a.filename } })} className="flex w-full items-center justify-center gap-1.5 border-t border-neutral-200 py-2 text-xs font-semibold text-[#8a1c34] hover:bg-neutral-100" data-testid={`release-asset-download-${i}`}>
                      <Download className="h-3.5 w-3.5" /> {c.download_hd}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Barra strumenti */}
          <div className="mt-8 flex flex-wrap gap-2.5">
            <Button onClick={downloadPdf} className="rounded-full bg-[#8a1c34] font-semibold text-white hover:bg-[#8a1c34]/90" data-testid="release-detail-official-pdf">
              <FileDown className="mr-1.5 h-4 w-4" /> {c.download_official_pdf}
            </Button>
            <Button variant="outline" onClick={copyText} className="rounded-full border-neutral-300 bg-transparent text-neutral-800 hover:bg-neutral-100" data-testid="release-detail-copy">
              <Copy className="mr-1.5 h-4 w-4" /> {c.copy_text}
            </Button>
            {release.zip_url && (
              <Button variant="outline" onClick={() => gate(() => triggerDownload(`${BACKEND}${release.zip_url}`), { type: "download", item: { kind: "release_zip", id: release?.id, title: release?.title, filename: "media-kit.zip" } })} className="rounded-full border-neutral-300 bg-transparent text-neutral-800 hover:bg-neutral-100" data-testid="release-detail-source-zip">
                <Download className="mr-1.5 h-4 w-4" /> {c.download_zip}
              </Button>
            )}
          </div>
        </article>
      </DialogContent>
    </Dialog>
  );
}
